import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowRight,
  AlertCircle,
  Building2,
  CheckCircle2,
  FileJson,
  KeyRound,
  Plus,
  Sparkles,
  Trash2,
  Users,
  UsersRound,
} from 'lucide-react';
import {
  type CreateModelProviderSecretRequest,
  type UpdateModelProviderSecretRequest,
} from '@reflex/shared';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { Card } from '@reflex/ui/components/ui/card';
import { PageDescription, PanelTitle } from '@reflex/ui/components/layout/Typography';
import { SectionNav } from '@reflex/ui/components/layout/SectionNav';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import {
  HowToUseParagraph,
  HowToUseSection,
  HowToUseStep,
  HowToUseSteps,
} from '@reflex/ui/components/integration/HowToUse';
import { IconSectionHeader } from '@/components/shared/IconSectionHeader';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { RequireActiveOrganization } from '@/components/shared/RequireActiveOrganization';
import { ModelProviderSecretsManager } from '@/components/secrets/ModelProviderSecretsManager';
import { RegisterClaudeMaxDialog } from '@/components/model-providers/RegisterClaudeMaxDialog';
import { RegisterCodexDialog } from '@/components/model-providers/RegisterCodexDialog';
import { useAuth } from '@/hooks/auth/useAuth';
import { useMyTeams } from '@/hooks/auth/useMyTeams';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useVisibleModelProviders } from '@/hooks/config/useVisibleModelProviders';

/**
 * Personal Model Providers page (`/{orgSlug}/security/model-providers`).
 *
 * Single entry point under the new Security drill for everything model-
 * key-related a user can manage at any tier:
 *   - Personal API keys (via `mps_*` user-tier rows).
 *   - Subscription credentials (Claude Max, Codex) — absorbed from the
 *     retired `CredentialsPage` so personal model-auth lives in one
 *     place instead of split between `/credentials` and the new page.
 *   - Outbound links to the team and org Model Providers pages for the
 *     teams the caller can manage and the active organization.
 *
 * The page is org-scoped because the underlying credential rows are
 * stored per-organization (each org keeps its own personal credential
 * set); switching the active org evicts the previous slot via
 * `useOrgKey()`.
 */
export function ModelProvidersPage({ embedded = false }: { embedded?: boolean }) {
  return (
    <RequireActiveOrganization title="Model Providers">
      {() => <ModelProvidersPageBody embedded={embedded} />}
    </RequireActiveOrganization>
  );
}

function ModelProvidersPageBody({ embedded = false }: { embedded?: boolean }) {
  const orgKey = useOrgKey();
  const queryClient = useQueryClient();
  const routes = useOrgRoutes();
  const { can } = useAuth();
  const teams = useMyTeams();
  const [addKeyOpen, setAddKeyOpen] = useState(false);

  const mpsKey = queryKeys.myModelProviderSecrets(orgKey);

  const mpsQuery = useQuery({
    queryKey: mpsKey,
    queryFn: api.listMyModelProviderSecrets,
  });

  // Create/update surface failures inline inside the manager's dialog
  // (which awaits the mutation and stays open on rejection), so they do
  // not also raise an error toast — only the success path notifies.
  const createMutation = useMutation({
    mutationFn: (data: CreateModelProviderSecretRequest) => api.createMyModelProviderSecret(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mpsKey });
      toast.success('Model provider key saved');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      secretId,
      data,
    }: {
      secretId: string;
      data: UpdateModelProviderSecretRequest;
    }) => api.updateMyModelProviderSecret(secretId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mpsKey });
      toast.success('Model provider key updated');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (secretId: string) => api.deleteMyModelProviderSecret(secretId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mpsKey });
      toast.success('Model provider key deleted');
    },
    onError: (err: Error) =>
      toast.error('Failed to delete model provider key', { description: err.message }),
  });

  const isPending =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // The server lists every mps row the caller can read (user/team/org);
  // restrict the manager to the user-tier rows so the team/org slots
  // stay surfaced through their dedicated pages instead of being
  // editable here.
  const userSecrets = (mpsQuery.data ?? []).filter((s) => s.scope === 'user');
  const visibleProviders = useVisibleModelProviders(userSecrets);

  const canManageOrgSecrets = can('secrets:manage');

  const navSections = useMemo(
    () => [
      { id: 'your-keys', label: 'Your API Keys', icon: Sparkles },
      { id: 'subscriptions', label: 'Subscriptions', icon: KeyRound },
      { id: 'team-keys', label: 'Team Keys', icon: UsersRound },
      { id: 'org-keys', label: 'Organization Keys', icon: Building2 },
    ],
    [],
  );

  const mainContent = (
    <div className={embedded ? 'space-y-8' : 'mt-2 space-y-8'}>
      <div id="your-keys" className="scroll-mt-24">
        <ModelProviderSecretsManager
          title="Your API Keys"
          description="Personal model-provider keys, available only to agents you launch and never visible to organization administrators. Personal keys take precedence over team and org keys for runs under your account."
          secrets={userSecrets}
          providers={visibleProviders}
          isLoading={mpsQuery.isLoading}
          isPending={isPending}
          showSectionAction={embedded}
          addOpen={addKeyOpen}
          onAddOpenChange={setAddKeyOpen}
          emptyState="No personal model-provider keys yet. Add one to use your own quota for agent launches."
          onCreate={(provider, name, value, _scopeChoice, baseUrl) =>
            createMutation.mutateAsync({
              provider,
              type: 'apiKey',
              name,
              value,
              baseUrl,
            })
          }
          onUpdate={(secretId, patch) => updateMutation.mutateAsync({ secretId, data: patch })}
          onDelete={(secretId) => deleteMutation.mutate(secretId)}
        />
      </div>

      <SubscriptionsSection />

      {!embedded && (
        <>
          <section
            id="team-keys"
            className="scroll-mt-24 space-y-3"
            data-testid="team-keys-section"
          >
            <IconSectionHeader icon={UsersRound} className="mb-2 gap-3">
              Team Keys
            </IconSectionHeader>
            {teams.length === 0 ? (
              <PageDescription className="mt-0">
                You are not on any team in this organization. Team-owned model keys appear here once
                an admin adds you to a team.
              </PageDescription>
            ) : (
              <Card className="divide-y p-2">
                {teams.map((team) => (
                  <Link
                    key={team.ownerTeamId}
                    to={routes.credentialsHubTeam(team.ownerTeamId, 'model-providers')}
                    className="flex items-center justify-between gap-3 rounded-md px-3 py-3 text-sm hover:bg-accent/40"
                    data-testid={`team-keys-link-${team.ownerTeamId}`}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate font-medium">{team.name}</span>
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  </Link>
                ))}
              </Card>
            )}
          </section>

          <section id="org-keys" className="scroll-mt-24 space-y-3" data-testid="org-keys-section">
            <IconSectionHeader icon={Building2} className="mb-2 gap-3">
              Organization Keys
            </IconSectionHeader>
            <PageDescription className="mt-0">
              Organization-tier keys are the fallback every agent uses when neither the launching
              user nor an applicable team supplies a key for the provider. Updated keys apply to
              agents launched after the change.
            </PageDescription>
            <Card className="p-2">
              <Link
                to={routes.credentialsHubSection('organization', 'model-providers')}
                className="flex items-center justify-between gap-3 rounded-md px-3 py-3 text-sm hover:bg-accent/40"
                data-testid="org-keys-link"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate font-medium">
                    {canManageOrgSecrets
                      ? 'Manage organization model keys'
                      : 'View organization model keys'}
                  </span>
                </div>
                <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </Link>
            </Card>
          </section>
        </>
      )}
    </div>
  );

  if (embedded) return mainContent;

  return (
    <PageLayout maxWidth="full">
      <div className="mx-auto flex w-full max-w-6xl gap-10">
        <div className="min-w-0 flex-1 max-w-4xl">
          <PageHeader
            iconVariant="box"
            icon={<Sparkles className="h-5 w-5 text-muted-foreground" />}
            title="Model Providers"
            description="Manage every model credential you can access: your personal keys, subscriptions, and the team and organization keys you have permission to manage."
            docs="administration/security#secrets-tiers"
            actions={
              <div className="flex items-center gap-2">
                <Button
                  aria-label="Add key"
                  disabled={isPending}
                  onClick={() => setAddKeyOpen(true)}
                >
                  <Plus />
                  <span className="sm:hidden">Add</span>
                  <span className="hidden sm:inline">Add key</span>
                </Button>
                <Button asChild variant="outline" size="sm">
                  <Link
                    to={routes.credentialsHubSection('organization', 'model-providers')}
                    aria-label="Organization Model Providers"
                  >
                    <Building2 className="h-3.5 w-3.5" />
                    <span className="max-sm:hidden">Org keys</span>
                  </Link>
                </Button>
              </div>
            }
            howToUse={{
              label: 'How model providers work',
              children: (
                <>
                  <HowToUseSection title="Personal keys">
                    <HowToUseParagraph>
                      Keys on this page are yours alone. When you launch an agent, personal keys
                      take precedence over team and organization keys for the same provider.
                    </HowToUseParagraph>
                  </HowToUseSection>
                  <HowToUseSection title="Subscriptions">
                    <HowToUseSteps>
                      <HowToUseStep>
                        Register Claude Max or Codex subscription credentials in the Subscriptions
                        section so agents can use those accounts.
                      </HowToUseStep>
                      <HowToUseStep>
                        Team and organization keys live on their own pages. Use Org keys or a team
                        link when you need shared credentials.
                      </HowToUseStep>
                    </HowToUseSteps>
                  </HowToUseSection>
                </>
              ),
            }}
          />

          {mainContent}
        </div>
        <aside className="hidden w-44 shrink-0 xl:block">
          <div className="sticky top-32">
            <SectionNav sections={navSections} data-testid="model-providers-section-nav" />
          </div>
        </aside>
      </div>
    </PageLayout>
  );
}

/**
 * Subscription credential cards — Claude Max and Codex — moved here
 * verbatim from the retired `CredentialsPage`. Subscription auth is a
 * paste-JSON-from-local-file flow rather than an API key, so it can't
 * share the {@link ModelProviderSecretsManager} surface and lives in
 * its own section. Org/team tiers don't support subscription
 * credentials (today), so this section is personal-only by design.
 */
function SubscriptionsSection() {
  const queryClient = useQueryClient();
  const orgKey = useOrgKey();
  const userCredentialsKey = queryKeys.userCredentials(orgKey);
  const claudeMaxStatusKey = queryKeys.claudeMaxStatus(orgKey);
  const codexSubscriptionStatusKey = queryKeys.codexSubscriptionStatus(orgKey);

  // A user can register several Claude Max subscriptions, so the dialog tracks
  // which one it is acting on: `undefined` registers a new subscription, a set
  // alias updates that existing one in place.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAlias, setEditingAlias] = useState<string | undefined>(undefined);
  const [codexDialogOpen, setCodexDialogOpen] = useState(false);

  function openRegisterDialog() {
    setEditingAlias(undefined);
    setDialogOpen(true);
  }

  function openUpdateDialog(alias: string) {
    setEditingAlias(alias);
    setDialogOpen(true);
  }

  const credentialsQuery = useQuery({
    queryKey: userCredentialsKey,
    queryFn: api.getUserCredentials,
  });

  const codexSubscriptionStatusQuery = useQuery({
    queryKey: codexSubscriptionStatusKey,
    queryFn: api.getCodexSubscriptionStatus,
  });

  const deleteMutation = useMutation({
    // The default subscription deletes with no alias (matching the historical
    // request shape); named subscriptions pass their alias through.
    mutationFn: (alias: string) => api.deleteClaudeMax(alias === 'default' ? undefined : alias),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userCredentialsKey });
      void queryClient.invalidateQueries({ queryKey: claudeMaxStatusKey });
      toast.success('Claude Max credential deleted');
    },
    onError: (error: Error) =>
      toast.error('Failed to delete credential', { description: error.message }),
  });

  const deleteCodexMutation = useMutation({
    mutationFn: () => api.deleteCodexSubscription(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userCredentialsKey });
      void queryClient.invalidateQueries({ queryKey: codexSubscriptionStatusKey });
      toast.success('Codex subscription credential deleted');
    },
    onError: (error: Error) =>
      toast.error('Failed to delete credential', { description: error.message }),
  });

  const credentials = credentialsQuery.data?.credentials ?? [];
  const claudeMaxCreds = credentials.filter((c) => c.kind === 'claude-max');
  const codexSubscriptionConfigured = codexSubscriptionStatusQuery.data?.configured ?? false;

  return (
    <section
      id="subscriptions"
      className="scroll-mt-24 space-y-4"
      data-testid="subscriptions-section"
    >
      <IconSectionHeader icon={KeyRound} className="mb-2 gap-3">
        Subscriptions
      </IconSectionHeader>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Private to you</AlertTitle>
        <AlertDescription>
          Subscription credentials are encrypted and stored securely. They are never visible to
          organization administrators or other users.
        </AlertDescription>
      </Alert>

      <SectionCard
        title="Claude Max"
        description="Use your personal Claude Max subscriptions for agent launches instead of organization API keys. Register more than one and pick which to use when launching an agent."
        data-testid="claude-max-card"
        action={
          claudeMaxCreds.length > 0 ? (
            // When at least one subscription exists the primary affordance is
            // adding another; it carries the tutorial id so the claude-max
            // tour can target whichever register button is on screen.
            <Button
              variant="outline"
              size="sm"
              data-tutorial-id="claude-max-register"
              onClick={openRegisterDialog}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add subscription
            </Button>
          ) : undefined
        }
      >
        {claudeMaxCreds.length > 0 ? (
          <div className="space-y-3">
            {claudeMaxCreds.map((cred) => {
              const label = cred.alias === 'default' ? 'Default' : cred.alias;
              const deleting = deleteMutation.isPending && deleteMutation.variables === cred.alias;
              return (
                <div
                  key={cred.alias}
                  data-testid={`claude-max-subscription-${cred.alias}`}
                  className="flex items-center justify-between gap-3 rounded-lg border p-3"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <FileJson className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <PanelTitle as="span" className="text-sm">
                        {label}
                      </PanelTitle>
                      <PageDescription className="mt-0 text-xs">
                        {cred.updatedAt
                          ? `Updated ${new Date(cred.updatedAt).toLocaleDateString()}`
                          : 'Configured'}
                      </PageDescription>
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openUpdateDialog(cred.alias)}
                    >
                      Update
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Delete ${label} Claude Max subscription`}
                      onClick={() => deleteMutation.mutate(cred.alias)}
                      disabled={deleting}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-4">
            <PageDescription className="mt-0">
              No Claude Max subscription configured. Register your credential to use your personal
              Claude Max subscription for agent launches.
            </PageDescription>
            <Button size="sm" data-tutorial-id="claude-max-register" onClick={openRegisterDialog}>
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Register Credential
            </Button>
          </div>
        )}
        <RegisterClaudeMaxDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          alias={editingAlias}
        />
      </SectionCard>

      <SectionCard
        title="Codex"
        description="Use your personal Codex subscription for Codex agent launches instead of organization OpenAI API keys."
      >
        {codexSubscriptionConfigured ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Badge variant="default" className="gap-1.5">
                <CheckCircle2 className="h-3 w-3" />
                Configured
              </Badge>
              <PageDescription className="mt-0">
                Your Codex credential is ready to use.
              </PageDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setCodexDialogOpen(true)}>
                Update Credential
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => deleteCodexMutation.mutate()}
                disabled={deleteCodexMutation.isPending}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                {deleteCodexMutation.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <PageDescription className="mt-0">
              No Codex subscription credential configured. Register your local Codex auth file to
              use your personal subscription for Codex agent launches.
            </PageDescription>
            <Button size="sm" onClick={() => setCodexDialogOpen(true)}>
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Register Credential
            </Button>
          </div>
        )}
        <RegisterCodexDialog open={codexDialogOpen} onOpenChange={setCodexDialogOpen} />
      </SectionCard>

      <SectionCard title="How to get your Claude Max credential">
        <div className="space-y-4 text-sm">
          <p>Claude Max stores your OAuth credentials locally. To register your credential:</p>
          <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
            <li>
              Open Claude Code and run <code className="rounded bg-muted px-1 py-0.5">/login</code>{' '}
              to sign in with your Claude Max subscription. Do this every time before registering or
              updating your credential here. The token captured in this flow does not refresh inside
              the devbox the way it does on your local machine, so starting from a fresh login
              maximizes how long it stays valid.
            </li>
            <li>
              Run the command from the registration dialog to retrieve your credential JSON. On
              macOS it reads from the Keychain; on Linux it reads from{' '}
              <code className="rounded bg-muted px-1 py-0.5">~/.claude/.credentials.json</code>; on
              Windows it reads from{' '}
              <code className="rounded bg-muted px-1 py-0.5">
                %USERPROFILE%\.claude\.credentials.json
              </code>{' '}
              (run in PowerShell).
            </li>
            <li>Paste the output into the registration dialog.</li>
          </ol>
          <Alert variant="default" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Your credential is encrypted at rest and only accessible to you. It is mounted
              directly into your devbox when launching agents, so no API keys are exposed.
            </AlertDescription>
          </Alert>
        </div>
      </SectionCard>

      <SectionCard title="How to get your Codex credential">
        <div className="space-y-4 text-sm">
          <p>Codex stores your signed-in account credential locally.</p>
          <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
            <li>Run Codex locally and sign in with your subscription account.</li>
            <li>
              Copy the contents of{' '}
              <code className="rounded bg-muted px-1 py-0.5">~/.codex/auth.json</code>.
            </li>
            <li>Paste the JSON into the Codex registration dialog.</li>
          </ol>
        </div>
      </SectionCard>
    </section>
  );
}
